import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { format } from "date-fns";
import { ar } from "date-fns/locale";
import { Package, Monitor, Key, Calendar, FileText, Shield, User, Building } from "lucide-react";

interface EmployeeAssetsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  employee: any;
}

export const EmployeeAssetsDialog = ({
  open,
  onOpenChange,
  employee
}: EmployeeAssetsDialogProps) => {
  if (!employee) return null;
  
  const getAssetTypeIcon = (type: string) => {
    switch (type) {
      case 'physical':
        return <Package className="w-4 h-4" />;
      case 'digital':
        return <Monitor className="w-4 h-4" />;
      case 'credentials':
        return <Key className="w-4 h-4" />;
      default:
        return <Package className="w-4 h-4" />;
    }
  };
  
  const getAssetTypeLabel = (type: string) => {
    switch (type) {
      case 'physical':
        return 'مادية';
      case 'digital':
        return 'رقمية';
      case 'credentials':
        return 'بيانات دخول';
      default:
        return type;
    }
  };
  
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'assigned':
        return 'bg-green-100 text-green-800';
      case 'returned':
        return 'bg-gray-100 text-gray-800';
      case 'lost':
        return 'bg-red-100 text-red-800';
      case 'damaged':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  
  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'assigned':
        return 'مسلمة';
      case 'returned':
        return 'مُرجعة';
      case 'lost':
        return 'مفقودة';
      case 'damaged':
        return 'تالفة';
      default:
        return status;
    }
  };
  
  const getConditionLabel = (condition: string) => {
    switch (condition) {
      case 'new':
        return 'جديد';
      case 'good':
        return 'جيد';
      case 'fair':
        return 'مقبول';
      case 'poor':
        return 'ضعيف';
      case 'damaged':
        return 'تالف';
      default:
        return condition;
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh]" dir="rtl">
        <DialogHeader>
          <DialogTitle className="text-xl">تفاصيل عهد الموظف</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Employee Info */}
          <div className="bg-muted/50 rounded-lg p-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">الاسم</p>
                  <p className="font-medium">{employee.name}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">رقم الموظف</p>
                  <p className="font-medium">{employee.id.slice(0, 8)}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Building className="w-4 h-4 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">القسم</p>
                  <p className="font-medium">{employee.department || "غير محدد"}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">إجمالي العهد</p>
                  <p className="font-medium">{employee.totalAssets} عهدة</p>
                </div>
              </div>
            </div>
          </div>
          
          <Separator />
          
          {/* Assets List */}
          <ScrollArea className="h-[400px]">
            <div className="space-y-3">
              {employee.assets && employee.assets.length > 0 ? (
                employee.assets.map((asset: any) => (
                  <div key={asset.id} className="border rounded-lg p-4 space-y-3">
                    <div className="flex justify-between items-start">
                      <div className="flex items-start gap-3">
                        <div className="p-2 rounded-lg bg-primary/10">
                          {getAssetTypeIcon(asset.asset_type)}
                        </div>
                        <div>
                          <h4 className="font-medium">{asset.asset_name}</h4>
                          {asset.asset_code && (
                            <p className="text-sm text-muted-foreground">
                              كود: {asset.asset_code}
                            </p>
                          )}
                          {asset.category?.name_ar && (
                            <p className="text-sm text-muted-foreground">
                              التصنيف: {asset.category.name_ar}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col gap-2 items-end">
                        <Badge variant="secondary">
                          {getAssetTypeLabel(asset.asset_type)}
                        </Badge>
                        <Badge className={getStatusColor(asset.status)}>
                          {getStatusLabel(asset.status)}
                        </Badge>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                      {asset.serial_number && (
                        <div>
                          <p className="text-muted-foreground">الرقم التسلسلي</p>
                          <p className="font-medium">{asset.serial_number}</p>
                        </div>
                      )}
                      
                      {asset.assigned_date && (
                        <div>
                          <p className="text-muted-foreground">تاريخ التسليم</p>
                          <p className="font-medium">
                            {format(new Date(asset.assigned_date), "dd MMMM yyyy", { locale: ar })}
                          </p>
                        </div>
                      )}
                      
                      {asset.return_date && (
                        <div>
                          <p className="text-muted-foreground">تاريخ الإرجاع المتوقع</p>
                          <p className="font-medium">
                            {format(new Date(asset.return_date), "dd MMMM yyyy", { locale: ar })}
                          </p>
                        </div>
                      )}
                      
                      {asset.condition && (
                        <div>
                          <p className="text-muted-foreground">الحالة</p>
                          <p className="font-medium">{getConditionLabel(asset.condition)}</p>
                        </div>
                      )}
                      
                      {asset.assigned_by_employee?.name && (
                        <div>
                          <p className="text-muted-foreground">سلمها</p>
                          <p className="font-medium">{asset.assigned_by_employee.name}</p>
                        </div>
                      )}
                    </div>
                    
                    {asset.description && (
                      <div className="pt-2 border-t">
                        <p className="text-sm text-muted-foreground">الوصف</p>
                        <p className="text-sm">{asset.description}</p>
                      </div>
                    )}
                    
                    {asset.notes && (
                      <div className="pt-2 border-t">
                        <p className="text-sm text-muted-foreground">ملاحظات</p>
                        <p className="text-sm">{asset.notes}</p>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <Package className="w-12 h-12 mx-auto mb-3 text-muted-foreground" />
                  <p className="text-muted-foreground">لا توجد عهد مسلمة لهذا الموظف</p>
                </div>
              )}
            </div>
          </ScrollArea>
        </div>
      </DialogContent>
    </Dialog>
  );
};